// ShippingProtectionGuide.tsx
import Footer from "@/components/landing/layout/Footer";
import Header from "@/components/landing/layout/Header";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

/**
 * Tailwind uses `tw-` prefix in your project; kept as-is.
 * FAQ content preserved exactly; each item is collapsible with animation.
 */

type FaqItem = {
    q: string;
    a: React.ReactNode;
};

const FAQS: FaqItem[] = [
    {
        q: "What is shipping protection?",
        a: (
            <>
                <p>
                    If you have purchased shipping protection from your retailer, your package is protected in case of loss,
                    damage, or theft during shipment. Shipping protection protects packages shipped within India. Protega administers shipping protection on behalf of the retailer.
                </p>
            </>
        ),
    },
    {
        q: "When does my protection start and end?",
        a: (
            <>
                <p>
                    Shipping protection starts once the package is shipped and ends when the package is in your hands, or
                    once the issue is resolved. Shipping protection ends when:
                </p>
                <ul className="tw-list-disc tw-list-inside tw-mt-2">
                    <li>Your package is delivered to your final destination undamaged</li>
                    <li>Your package doesn't reach you, you file a claim, and that claim is approved or denied</li>
                    <li>
                        Your package arrives damaged, you file a claim, and that claim is approved or denied. Your package is
                        protected from damage incurred during shipment and delivery. You have 30 days after delivery to submit a
                        claim. Damage incurred after delivery is not protected. Damage reported 31 days or more after delivery is
                        considered incurred after delivery and is not protected.
                    </li>
                </ul>
            </>
        ),
    },
    {
        q: "How do I submit a claim?",
        a: (
            <>
                <p>
                    It’s simple. Contact Protega online 24/7 or by phone. Most issues are resolved in minutes and after approval,
                    Protega will send you all the details to resolve your issue.
                </p>
            </>
        ),
    },
    {
        q: "What happens if my package is lost, damaged, or stolen?",
        a: (
            <>
                <p>
                    After approval, if your package is lost or stolen, Protega will provide a gift card for the value of the product
                    (plus shipping costs and taxes) or will arrange a replacement. If your product arrives damaged and depending on the
                    level of damage, a full or partial refund via a gift card may be provided or the product may be repaired. In some
                    instances, a replacement may be shipped directly to you.
                </p>
            </>
        ),
    },
    {
        q: "What do I need to file a claim?",
        a: (
            <>
                <p>
                    We’ll ask you to tell us if the item(s) were lost, stolen, or damaged. We may ask for details and documents including:
                </p>
                <ul className="tw-list-disc tw-list-inside tw-mt-2">
                    <li>Photos of the damaged items, packages, or packing material</li>
                    <li>A police report or video surveillance footage (if requested)</li>
                </ul>
                <p>
                    If you aren’t able to provide these documents within 30 days, we may deny your claim.
                </p>
            </>
        ),
    },
    {
        q: "When can I file a claim?",
        a: (
            <>
                <div className="tw-overflow-auto tw-rounded tw-border tw-border-gray-700 tw-mt-3">
                    <table className="tw-w-full tw-text-left tw-text-base">
                        <thead className="tw-bg-[#0b0b0d]">
                            <tr>
                                <th className="tw-p-3 tw-border-r tw-w-1/3">If your package is...</th>
                                <th className="tw-p-3 tw-border-r">File a claim...</th>
                                <th className="tw-p-3">For example...</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="tw-border-t">
                                <td className="tw-p-3 tw-border-r">Damaged</td>
                                <td className="tw-p-3 tw-border-r">As soon as it is delivered</td>
                                <td className="tw-p-3">Broken parts</td>
                            </tr>
                            <tr className="tw-border-t">
                                <td className="tw-p-3 tw-border-r">Lost or stolen</td>
                                <td className="tw-p-3 tw-border-r">
                                    <ul className="tw-list-disc tw-ml-5">
                                        <li>5 days after your estimated delivery date (typically found on the retailer’s or the retailer’s shipper’s tracking site)</li>
                                        <li>For packages shipped outside the US, wait 20 days after your estimated delivery date</li>
                                    </ul>
                                </td>
                                <td className="tw-p-3">
                                    <ul className="tw-list-disc tw-ml-5">
                                        <li>“Pending” status without an estimated delivery date</li>
                                        <li>“In Transit” status and there are no updates after 5 days </li>
                                        <li>“Delivered” status and the package hasn't shown up for 5 days</li>
                                    </ul>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </>
        ),
    },
    {
        q: "What's not protected by shipping protection?",
        a: (
            <>
                <p>Not every item is eligible. Below are items that cannot be protected:</p>
                <ul className="tw-list-disc tw-list-inside tw-mt-2">
                    <li>Items without a cash value, free samples, or items included with your shipment at no cost</li>
                    <li>Items that never shipped (contact the retailer)</li>
                    <li>Digital items such as subscriptions, cloud storage, digital photographs, eBooks, audio files, gift cards, or software</li>
                    <li>Packages returned to sender or never picked up from carrier locations (unless otherwise protected by retailer)</li>
                </ul>

                <p className="tw-text-sm tw-text-gray-400 tw-mt-3">For order issues, contact the retailer directly, including:</p>
                <ul className="tw-list-disc tw-list-inside tw-mt-2">
                    <li>Backordered items</li>
                    <li>Missing items when package shows no exterior damage or tampered seal</li>
                    <li>Mechanical or electrical defects</li>
                </ul>

                <p className="tw-text-sm tw-text-gray-400 tw-mt-3">
                    Sometimes packages may not be in transit or delivered; in those cases, the package is still protected. A claim may be denied for the following reasons:
                </p>
                <ul className="tw-list-disc tw-list-inside tw-mt-2">
                    <li>If you provided the wrong shipping address</li>
                    <li>The recipient refused to sign or receive the package</li>
                    <li>Packages that are not delivered after multiple attempts by the shipping carrier</li>
                    <li>If the retailer or Protega determines the claim is fraudulent</li>
                </ul>
            </>
        ),
    },
    {
        q: "Can I cancel my shipping protection?",
        a: (
            <>
                <p>
                    If your order hasn't shipped, contact the retailer to adjust the order. If the order has already shipped, shipping protection is in effect and a refund cannot be provided.
                </p>

                <p className="tw-text-sm tw-text-gray-400 tw-mt-2">
                    Protega may cancel shipping protection in cases of fraud, material misrepresentation, or non-payment. Should that occur,
                    Protega will notify affected individuals and will refund amounts paid for shipping protection that has been rescinded.
                </p>
            </>
        ),
    },
    {
        q: "Can I transfer my shipping protection to another item I bought?",
        a: (
            <>
                <p>
                    No, shipping protection is not transferable to other items not originally protected at the time of purchase. If you want to add shipping protection to a recent purchase that hasn't shipped yet, contact the retailer.
                </p>
            </>
        ),
    },
];

const AccordionItem: React.FC<{
    item: FaqItem;
    index: number;
    isOpen: boolean;
    onToggle: (i: number) => void;
}> = ({ item, index, isOpen, onToggle }) => {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [maxH, setMaxH] = useState<string>("0px");

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        if (isOpen) {
            // expand -> set explicit px then remove after animation
            setMaxH(`${el.scrollHeight}px`);
            const t = setTimeout(() => setMaxH("none"), 310);
            return () => clearTimeout(t);
        } else {
            // collapse -> if currently none, set measured px then animate to 0
            if (maxH === "none") {
                setMaxH(`${el.scrollHeight}px`);
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => setMaxH("0px"));
                });
            } else {
                setMaxH("0px");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    return (
        <div >
            <h3>
                <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={`faq-${index}`}
                    onClick={() => onToggle(index)}
                    className="tw-w-full tw-text-left tw-py-4 tw-flex tw-items-start tw-justify-between tw-gap-4 tw-focus:tw-outline-none"
                >
                    <span className="tw-text-xl tw-font-medium tw-text-[#00FFBD]">{item.q}</span>
                    <ChevronDown
                        className={`tw-h-5 tw-w-5 tw-transition-transform tw-duration-300 ${isOpen ? "tw-rotate-180" : "tw-rotate-0"}`}
                        aria-hidden
                    />
                </button>
            </h3>

            <div
                id={`faq-${index}`}
                ref={contentRef}
                style={{
                    maxHeight: maxH === "none" ? undefined : maxH,
                    transition: "max-height 300ms ease, opacity 200ms ease",
                    opacity: isOpen ? 1 : 0,
                    overflow: "hidden",
                }}
                aria-hidden={!isOpen}
                className="tw-text-base tw-text-gray-300 tw-pr-2 tw-pl-0"
            >
                <div className="tw-pt-2 tw-pb-6">{item.a}</div>
            </div>
        </div>
    );
};

const ShippingProtectionGuide: React.FC = () => {
    const [openIndex, setOpenIndex] = useState<number | null>(); // first open by default if desired, set to null to start all closed

    const toggleIndex = (i: number) => {
        setOpenIndex((prev) => (prev === i ? null : i));
    };

    return (
        <>
            <Header />
            <main className="tw-bg-black tw-min-h-screen tw-text-white tw-font-sans">
                {/* HERO */}
                <section className="tw-relative tw-h-[420px] md:tw-h-[360px] lg:tw-h-[520px] tw-flex tw-items-center tw-justify-center">
                    <img
                        src="/ShippingFAQ/ReadMoreHero.png"
                        alt="hero"
                        className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover tw-opacity-90"
                    />

                    <div className="tw-absolute tw-left-6 md:tw-left-12 lg:tw-left-20 tw-top-20 lg:tw-top-28 tw-max-w-xl tw-p-8 tw-bg-[#0B0F11]/80 tw-rounded-lg tw-shadow-lg">
                        <div className=" tw-text-xs tw-text-[#00FFBD] tw-font-medium tw-flex tw-items-center tw-gap-2">
                            <img src="/ShippingFAQ/shipping.png" alt="shipping" />
                            <div className="tw-flex tw-flex-col tw-justify-center">
                                <p>Protega </p>
                                <span className="tw-text-lg">Shipping Protection</span>
                            </div>
                        </div>

                        <h1 className="tw-text-2xl md:tw-text-4xl tw-font-semibold tw-mt-3">Protega Shipping <br /> Protection Guide</h1>
                        <p className="tw-text-lg tw-text-gray-300 tw-mt-3">
                            Protect your package in case of loss, damage, or theft.
                        </p>

                        <div className="tw-mt-5">
                            <Link
                                to="/customer/claims"
                                className="tw-inline-block tw-bg-[#00F3A3] tw-text-black tw-font-medium tw-px-4 tw-py-2 tw-rounded-full hover:tw-opacity-95"
                            >
                                File a Claim
                            </Link>
                        </div>
                    </div>
                </section>

                {/* FAQ container */}
                <section className="tw-max-w-5xl tw-mx-auto tw-py-16 tw-px-6">
                    <h2 className="tw-text-3xl tw-font-semibold tw-mb-8 tw-text-white">Frequently Asked Questions</h2>

                    {/* Accordion list */}
                    <div className="tw-text-sm tw-text-gray-300 tw-space-y-6">
                        {FAQS.map((item, idx) => (
                            <AccordionItem key={idx} item={item} index={idx} isOpen={openIndex === idx} onToggle={toggleIndex} />
                        ))}
                    </div>

                    {/* CTA footer inside FAQ area */}
                    {/* Replace the existing CTA div with this */}
                    <div className="tw-mt-12 tw-rounded-3xl tw-overflow-hidden tw-bg-[#061011]">
                        <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-justify-between">
                            {/* Left big green card (image + text) */}
                            <div className="tw-w-full md:tw-w-1/2  tw-flex  tw-rounded-3xl tw-items-center tw-gap-6 tw-p-8 md:tw-p-16 tw-bg-[#00F3A3]">
                                {/* Replace src with your actual image asset */}
                                <img
                                    src="/ShippingFAQ/shippingActive.png"
                                    alt="Protega Shipping Protection"
                                />
                                <div>
                                    <p className="tw-text-sm tw-text-black tw-font-medium">Protega</p>
                                    <p className="tw-text-2xl md:tw-text-3xl tw-font-semibold tw-text-black">Shipping Protection</p>
                                </div>
                            </div>

                            {/* Right content: heading + buttons */}
                            <div className="tw-w-full md:tw-w-1/2  tw-px-6 tw-py-5 tw-flex tw-flex-col md:tw-items-start tw-items-center tw-justify-center tw-gap-2">
                                <div className="tw-text-center tw-w-full">
                                    <h3 className="tw-text-2xl md:tw-text-2xl tw-font-bold tw-text-white">Have more questions?</h3>
                                    <p className="tw-text-2xl md:tw-text-2xl tw-font-bold tw-text-white">We are here to help.</p>
                                </div>

                                <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-mt-2">
                                    <Link
                                        to="/contact-us"
                                        className="tw-inline-block tw-bg-[#00F3A3] tw-text-black tw-font-medium tw-rounded-full tw-px-8 tw-py-3 tw-text-center hover:tw-opacity-95"
                                    >
                                        Contact Support
                                    </Link>

                                    <Link
                                        to="/customer/claims"
                                        className="tw-inline-block tw-border tw-border-white tw-text-white tw-rounded-full tw-px-8 tw-py-3 tw-text-center hover:tw-bg-white hover:tw-text-black"
                                    >
                                        File a Claim
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                </section>

                <Footer />
            </main>
        </>
    );
};

export default ShippingProtectionGuide;
